import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatDate, enrollmentBalance, type CourseEdition, type Enrollment } from "@/lib/api";

export default function EvaluadorCursoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [edition, setEdition] = useState<CourseEdition | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [evalMap, setEvalMap] = useState<Record<string, string>>({});
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [certsIssued, setCertsIssued] = useState(0);
  const [rowErrors, setRowErrors] = useState<string[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const editionData = await api.getEdition(id);
        setEdition(editionData);
        const enrs = editionData.enrollments ?? [];
        setEnrollments(enrs);
        const evalInit: Record<string, string> = {};
        const notesInit: Record<string, string> = {};
        enrs.forEach((e) => {
          evalInit[e.id] = e.evaluation_status;
          notesInit[e.id] = e.evaluator_notes;
        });
        setEvalMap(evalInit);
        setNotesMap(notesInit);
      } catch {
        setEdition(null);
        setEnrollments([]);
      }
      setLoading(false);
    })();
  }, [id]);

  const handleEval = (enrId: string, status: string) => {
    setEvalMap((prev) => ({ ...prev, [enrId]: status }));
    setSaved(false);
  };

  const handleNotesChange = (enrId: string, value: string) => {
    setNotesMap((prev) => ({ ...prev, [enrId]: value }));
  };

  const reloadEdition = async () => {
    if (!id) return;
    const editionData = await api.getEdition(id);
    setEdition(editionData);
    const enrs = editionData.enrollments ?? [];
    setEnrollments(enrs);
    const evalInit: Record<string, string> = {};
    const notesInit: Record<string, string> = {};
    enrs.forEach((e) => {
      evalInit[e.id] = e.evaluation_status;
      notesInit[e.id] = e.evaluator_notes;
    });
    setEvalMap(evalInit);
    setNotesMap(notesInit);
  };

  const saveEvaluations = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    setCertsIssued(0);
    setRowErrors([]);
    try {
      const toUpdate = enrollments.filter((enr) => {
        const evalStatus = evalMap[enr.id] ?? enr.evaluation_status;
        const notes = notesMap[enr.id] ?? enr.evaluator_notes;
        const changed = evalStatus !== enr.evaluation_status || notes !== enr.evaluator_notes;
        const needsCert = evalStatus === "aprobado" && !enr.certificate_issued;
        return changed || needsCert;
      });

      let issued = 0;
      const errors: string[] = [];
      for (const enr of toUpdate) {
        const evalStatus = evalMap[enr.id] ?? enr.evaluation_status;
        try {
          const updated = await api.updateEvaluation(enr.id, {
            evaluation_status: evalStatus,
            evaluator_notes: notesMap[enr.id] ?? enr.evaluator_notes,
          });
          if (evalStatus === "aprobado" && updated.certificate_issued && !enr.certificate_issued) {
            issued++;
          }
        } catch (err) {
          const name = enr.student?.full_name || enr.id;
          errors.push(`${name}: ${err instanceof Error ? err.message : "Error"}`);
        }
      }

      await reloadEdition();
      setCertsIssued(issued);
      setRowErrors(errors);
      setSaved(errors.length === 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar evaluaciones");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando edición...</p>
      </div>
    );
  }

  if (!edition) {
    return (
      <div className="text-center py-20">
        <p className="text-[var(--color-text-muted)]">Edición no encontrada</p>
        <button onClick={() => navigate("/portal-evaluador")} className="btn-primary mt-4 cursor-pointer">
          Volver
        </button>
      </div>
    );
  }

  const courseName = edition.course?.name || "Curso";
  const approvedCount = enrollments.filter(
    (e) => (evalMap[e.id] ?? e.evaluation_status) === "aprobado"
  ).length;
  const rejectedCount = enrollments.filter(
    (e) => (evalMap[e.id] ?? e.evaluation_status) === "rechazado"
  ).length;
  const reviewCount = enrollments.filter(
    (e) => (evalMap[e.id] ?? e.evaluation_status) === "revision"
  ).length;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/portal-evaluador")}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            Evaluación: {courseName}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            {edition.edition_label ? `${edition.edition_label} · ` : ""}
            {formatDate(edition.start_date)} &mdash; {formatDate(edition.end_date)} | {edition.location}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="card py-2 px-4 text-center">
          <span className="text-sm font-bold text-green-400">{approvedCount}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">Aprobados</span>
        </div>
        <div className="card py-2 px-4 text-center">
          <span className="text-sm font-bold text-red-400">{rejectedCount}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">Rechazados</span>
        </div>
        <div className="card py-2 px-4 text-center">
          <span className="text-sm font-bold text-yellow-400">{reviewCount}</span>
          <span className="text-xs text-[var(--color-text-muted)] ml-1">En Revisión</span>
        </div>
        <div className="ml-auto">
          <button
            onClick={saveEvaluations}
            disabled={saving}
            className="btn-primary flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-save-line"></i>
            {saving ? "Guardando..." : "Guardar Evaluaciones"}
          </button>
        </div>
      </div>

      {rowErrors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 space-y-1">
          <p className="font-medium">Algunos registros no se completaron:</p>
          {rowErrors.map((e) => <p key={e} className="text-xs">{e}</p>)}
        </div>
      )}

      {saved && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-3 flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-green-400 mt-0.5"></i>
          <p className="text-sm text-green-400">
            Evaluaciones guardadas correctamente.
            {certsIssued > 0 && ` Se emitieron ${certsIssued} certificado(s) automáticamente.`}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="bg-yellow-500/5 border border-yellow-500/15 rounded-xl p-4 flex items-start gap-3">
        <i className="ri-information-line text-yellow-400 mt-0.5"></i>
        <div>
          <p className="text-sm text-yellow-400 font-medium">Flujo de evaluación</p>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            Paso 1: Instructor registra asistencia → Paso 2: Usted revisa evidencias → Paso 3: Marca Aprobar/Rechazar/Revisión → Paso 4: Guardar. El certificado se emite al guardar solo si el estudiante tiene el curso pagado en su totalidad.
          </p>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="table-header">Estudiante</th>
                <th className="table-header">Asistencia</th>
                <th className="table-header">Obs. Instructor</th>
                <th className="table-header text-center">Aprobar</th>
                <th className="table-header text-center">Rechazar</th>
                <th className="table-header text-center">Revisión</th>
                <th className="table-header">Pago curso</th>
                <th className="table-header">Notas del Evaluador</th>
                <th className="table-header">Certificado</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enr) => {
                const evalStatus = evalMap[enr.id] ?? enr.evaluation_status;
                const notes = notesMap[enr.id] !== undefined ? notesMap[enr.id] : enr.evaluator_notes;
                const isApproved = evalStatus === "aprobado";
                const pendingIssue = isApproved && !enr.certificate_issued;
                const balance = enrollmentBalance(enr);
                const paymentBlocksCert = !balance.isFullyPaid;

                return (
                  <tr key={enr.id} className="hover:bg-[var(--color-bg-card-hover)] transition-colors">
                    <td className="table-cell">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-[var(--color-accent)]/15 flex items-center justify-center flex-shrink-0">
                          <span className="text-[var(--color-text-accent)] text-[10px] font-semibold">
                            {(enr.student?.full_name || "?").charAt(0)}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-[var(--color-text-primary)]">
                          {enr.student?.full_name || "Desconocido"}
                        </span>
                      </div>
                    </td>
                    <td className="table-cell">
                      {enr.attendance_status === "presente" ? (
                        <span className="badge-success">Presente</span>
                      ) : enr.attendance_status === "ausente" ? (
                        <span className="badge-danger">Ausente</span>
                      ) : (
                        <span className="badge-info">Pendiente</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="text-sm text-[var(--color-text-secondary)] max-w-[180px] truncate block">
                        {enr.observations || (
                          <span className="text-xs text-[var(--color-text-muted)] italic">Sin observaciones</span>
                        )}
                      </span>
                    </td>
                    <td className="table-cell text-center">
                      <button
                        onClick={() => handleEval(enr.id, "aprobado")}
                        disabled={enr.attendance_status !== "presente"}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          evalStatus === "aprobado"
                            ? "bg-green-500/20 text-green-400 border border-green-500/30"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-green-500/10 hover:text-green-400"
                        } ${enr.attendance_status !== "presente" ? "opacity-40 cursor-not-allowed" : ""}`}
                        title={enr.attendance_status !== "presente" ? "Requiere asistencia registrada" : "Aprobar estudiante"}
                      >
                        <i className="ri-check-line"></i>
                      </button>
                    </td>
                    <td className="table-cell text-center">
                      <button
                        onClick={() => handleEval(enr.id, "rechazado")}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          evalStatus === "rechazado"
                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-red-500/10 hover:text-red-400"
                        }`}
                        title="Rechazar estudiante"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </td>
                    <td className="table-cell text-center">
                      <button
                        onClick={() => handleEval(enr.id, "revision")}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          evalStatus === "revision"
                            ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:bg-yellow-500/10 hover:text-yellow-400"
                        }`}
                        title="Solicitar revisión"
                      >
                        <i className="ri-loop-left-line"></i>
                      </button>
                    </td>
                    <td className="table-cell">
                      {balance.isFullyPaid ? (
                        <span className={enr.payment_status === "cortesia" ? "badge-info" : "badge-success"}>
                          {enr.payment_status === "cortesia" ? "Cortesía" : "Pagado"}
                        </span>
                      ) : (
                        <div>
                          <span className="badge-danger">Pendiente</span>
                          <p className="text-[10px] text-red-400 mt-1">${balance.amountRemaining.toLocaleString("en-US")} USD</p>
                          {enr.payment_reference && (
                            <p className="text-[10px] font-mono text-[var(--color-text-muted)]">{enr.payment_reference}</p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      {editingNotes === enr.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={notes}
                            onChange={(e) => handleNotesChange(enr.id, e.target.value)}
                            className="input-field py-1.5 text-xs"
                            placeholder="Notas de evaluación..."
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setEditingNotes(null);
                              if (e.key === "Escape") setEditingNotes(null);
                            }}
                          />
                          <button
                            onClick={() => setEditingNotes(null)}
                            className="text-green-400 hover:text-green-300 cursor-pointer"
                          >
                            <i className="ri-check-line"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[var(--color-text-secondary)] max-w-[200px] truncate">
                            {notes || (
                              <span className="text-[var(--color-text-muted)] italic text-xs">
                                Sin notas
                              </span>
                            )}
                          </span>
                          <button
                            onClick={() => setEditingNotes(enr.id)}
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-accent)] cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="table-cell">
                      {enr.certificate_issued ? (
                        <span className="badge-success">Emitido</span>
                      ) : pendingIssue && paymentBlocksCert ? (
                        <span className="badge-danger text-[10px]" title="Saldo pendiente del curso">Bloq. pago</span>
                      ) : pendingIssue ? (
                        <span className="badge-warning text-[10px]">Al guardar</span>
                      ) : (
                        <span className="text-xs text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
